import React from 'react';
import { AlertCircle, ArrowDown, RefreshCw, Users } from 'lucide-react';
import type { FunnelRow } from '../../../services/MasterAdmin/dailyAnalyticsDashboardService';

const STEP_COPY: Record<string, { title: string; meaning: string }> = {
  'APP OPEN': { title: 'Opened the app / site', meaning: 'People who started a session.' },
  'AMBULANCE HOME': { title: 'Opened Ambulance', meaning: 'Entered the ambulance booking flow.' },
  'AGENCY LIST': { title: 'Saw ambulance agencies', meaning: 'Viewed nearby ambulance providers.' },
  'BOOKING FORM': { title: 'Opened booking form', meaning: 'Started filling pickup and patient details.' },
  'BOOKING INITIATED': { title: 'Submitted booking', meaning: 'Sent the ambulance request.' },
  'PAYMENT STARTED': { title: 'Started payment', meaning: 'Opened checkout / payment.' },
  'CONFIRMED': { title: 'Booking confirmed', meaning: 'Paid and confirmed successfully.' },
  'VIEW SERVICE': { title: 'Viewed a service', meaning: 'Opened a service page after landing.' },
  'ADD TO CART': { title: 'Added to cart', meaning: 'Added an item or opened cart.' },
  'CHECKOUT': { title: 'Reached checkout', meaning: 'Started checkout or booking.' },
  'PAYMENT SUCCESS': { title: 'Completed payment', meaning: 'Payment succeeded.' },
  'DOCTOR HOME': { title: 'Opened doctor consultation', meaning: 'Entered the consult doctors flow.' },
  'CLINIC SEARCH': { title: 'Searched clinics / doctors', meaning: 'Browsed available clinics or doctors.' },
  'CLINIC LIST': { title: 'Opened a clinic / doctor', meaning: 'Viewed a specific profile.' },
  'APPOINTMENT FORM': { title: 'Opened appointment form', meaning: 'Started booking a slot.' },
  'APPOINTMENT INITIATED': { title: 'Started appointment payment', meaning: 'Moved to payment for the consult.' },
  'APPOINTMENT CONFIRMED': { title: 'Appointment confirmed', meaning: 'Consult was booked successfully.' },
};

function funnelLabel(name: string): string {
  return String(name || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function stepCopy(stepName: string) {
  return STEP_COPY[stepName] || {
    title: funnelLabel(stepName),
    meaning: 'People who reached this step.',
  };
}

const ConversionFunnelPanel: React.FC<{
  funnel: FunnelRow[];
  selectedFunnel: string | null;
  onSelectFunnel: (name: string) => void;
  funnelDays: number;
  loadingFunnel: boolean;
  onChangeDays: (days: number) => void;
  onViewDropoffs: (funnelName: string, stepOrder: number, stepName: string) => void;
}> = ({
  funnel,
  selectedFunnel,
  onSelectFunnel,
  funnelDays,
  loadingFunnel,
  onChangeDays,
  onViewDropoffs,
}) => {
  const funnelNames = Array.from(new Set(funnel.map((row) => row.funnel_name)));
  const activeName = selectedFunnel || funnelNames[0] || '';
  const steps = funnel
    .filter((row) => row.funnel_name === activeName)
    .sort((a, b) => a.step_order - b.step_order);

  const started = Number(steps[0]?.users_count || 0);
  const completed = Number(steps[steps.length - 1]?.users_count || 0);
  const conversion = started > 0 ? (completed / started) * 100 : 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="flex flex-col gap-4 border-b border-gray-100 pb-4 dark:border-gray-700 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">How people move to a booking</h3>
          <p className="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
            Each row is one step. The number is unique people who did that step in the selected period — not a total of all events.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={activeName}
            onChange={(e) => onSelectFunnel(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          >
            {funnelNames.map((name) => (
              <option key={name} value={name}>{funnelLabel(name)}</option>
            ))}
          </select>
          <div className="flex rounded-lg bg-gray-100 p-1 dark:bg-gray-900">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => onChangeDays(days)}
                disabled={loadingFunnel}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${
                  funnelDays === days ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-700' : 'text-gray-500'
                }`}
              >
                {days} days
              </button>
            ))}
          </div>
          {loadingFunnel && <RefreshCw className="h-4 w-4 animate-spin text-primary-500" />}
        </div>
      </div>

      {loadingFunnel ? (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />
          ))}
        </div>
      ) : steps.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-400">No funnel activity in this period.</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_220px]">
          <div className="space-y-3">
            {steps.map((row, idx) => {
              const count = Number(row.users_count || 0);
              const prev = idx === 0 ? count : Number(steps[idx - 1].users_count || 0);
              const ofStart = started > 0 ? (count / started) * 100 : 0;
              const left = Math.max(0, prev - count);
              const leftPct = prev > 0 ? (left / prev) * 100 : 0;
              const copy = stepCopy(row.step_name);
              const weak = idx > 0 && leftPct >= 40;

              return (
                <div key={`${row.funnel_name}-${row.step_order}`}>
                  {idx > 0 && (
                    <div className="mb-3 flex items-center gap-2 pl-3 text-xs text-gray-500">
                      <ArrowDown className={`h-3.5 w-3.5 ${weak ? 'text-red-500' : 'text-gray-400'}`} />
                      {left > 0 ? (
                        <span>
                          <strong className={weak ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}>
                            {left.toLocaleString()} people left
                          </strong>
                          {' '}({leftPct.toFixed(0)}% of the previous step)
                        </span>
                      ) : (
                        <span>Everyone from the previous step continued</span>
                      )}
                    </div>
                  )}
                  <div className={`rounded-xl border p-4 ${weak ? 'border-red-200 dark:border-red-900/40' : 'border-gray-200 dark:border-gray-700'}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Step {idx + 1}</p>
                        <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">{copy.title}</p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{copy.meaning}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold tabular-nums text-gray-900 dark:text-white">{count.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{ofStart.toFixed(0)}% of people who started</p>
                      </div>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-900">
                      <div
                        className={`h-full rounded-full ${weak ? 'bg-red-500' : 'bg-primary-600'}`}
                        style={{ width: `${Math.max(ofStart, count > 0 ? 2 : 0)}%` }}
                      />
                    </div>
                    {idx < steps.length - 1 && left > 0 && (
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => onViewDropoffs(row.funnel_name, row.step_order, row.step_name)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline"
                        >
                          <Users className="h-3.5 w-3.5" />
                          See who left
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="h-fit rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Outcome</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{conversion.toFixed(1)}%</p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {completed.toLocaleString()} of {started.toLocaleString()} people finished
            </p>
            <div className={`mt-4 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${
              conversion < 5
                ? 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
            }`}>
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {conversion < 5
                  ? 'Most people drop before the last step. Check the biggest “people left” gap above.'
                  : 'This flow is converting. The biggest remaining drop is the next place to improve.'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversionFunnelPanel;
